import React, { useEffect, useState, useRef } from 'react';
import { Button, Card, Col, Form, Input, message, Modal, Row, Select } from 'antd';
import HTTPService from '../../utils/makerequest';
import { MailOutlined, UserOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { IPageProps } from '../../interfaces/page-data';
import { Toast } from 'primereact/toast';
import { StateLoader } from '../../redux/StateLoader';
import { IAuthentication, UserRole } from '../../interfaces/authentication';
import { ICompany } from '../../interfaces/ICompany';
import { useTranslation } from 'react-i18next';


const AddUser: React.FunctionComponent<IPageProps> = () => {
  const [companyData, setCompanyData] = useState<ICompany[]>();
  const [showPopup, setShowPopup] = useState(false); 
  const [popupMessage, setPopupMessage] = useState(''); 
  const { t } = useTranslation();
  const loader = new StateLoader();
  const userInfo: IAuthentication = loader.loadAuthState();
  const toast = useRef<any>(null);
  

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const result = await HTTPService.GETCompanyNames();
      setCompanyData(result);
    } catch (error) {
      console.error('Failed to fetch company data', error);
    }
  };

  const Register = (value) => {
    let companyIdToUse;
    if (userInfo?.role === UserRole.AltisAdmin) {
      companyIdToUse = value.selectedCompany;
    } else {
      companyIdToUse = userInfo?.companyId;
    }
  
    const newUser = {
      companyId: companyIdToUse,
      userEmail: value.userEmail,
      firstName: value.firstName,
      lastName: value.lastName,
      role: value.role,
      personelStatus: value.personelStatus,
    };
  
    HTTPService.POST(`/User/registerUser`, newUser)
      .then((response) => {
        if (response.status === 200) {
          if (response.data) {
            setPopupMessage(response.data);
            setShowPopup(true); 
          }
        } else if (response.status === 450) {
          toast.current?.show({
            severity: 'error',
            summary: t('errors'),
            detail: t(response.data),
            life: 3000
          });
        } else if (response.status === 204) {
          toast.current?.show({
            severity: 'success',
            summary: t('successful'),
            detail: t('register_success'),
            life: 3000
          });
          setTimeout(() => {
            handleRedirect(); 
          }, 1000);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: t('errors'),
            detail: t(response.data),
            life: 3000
          });
        }
      })
      .catch((error) => {
        console.error('User registration failed: ', error);
        toast.current?.show({
          severity: 'error',
          summary: t('errors'),
          detail: t('register_failure'),
          life: 3000
        });
      });
  };
  
  const handleCancel = () => {
    window.location.replace('/admin/user-settings/');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(popupMessage)
      .then(() => {
        message.success(t('password_copy'));
      })
      .catch(err => {
        message.error(t('password_copy_fail'));
      });
  };

  const handleRedirect = () => {
    window.location.replace('/admin/user-settings');
  };
  

  return (
    <Card style={{ padding: '50px' }}>
      <Toast ref={toast} />
      <h3>{t('register_user')}</h3>
      <Form layout='vertical' onFinish={Register}>
        {userInfo?.role === UserRole.AltisAdmin ? (
          <Form.Item
            name='selectedCompany'
            label={t('Select company')}
            rules={[{ required: true, message: t('please_select_company') }]}>
            <Select showSearch placeholder={t('company_name')}>
              {companyData?.map((company) => (
                <Select.Option key={company.companyId} value={company.companyId}>
                  {company.companyName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        ) : null}
        <Form.Item
          name='userEmail'
          label={t('email')}
          rules={[
            { required: true, message: t('please_input_email') },
            { pattern: /^[^\s@]+@[^\s@]+$/, message: t('email_invalid') },
          ]}>
          <Input prefix={<MailOutlined />} placeholder={t('email')} style={{ borderRadius: 0 }} />
        </Form.Item>
        <Form.Item
          name='firstName'
          label={t('firstName')}
          rules={[
            { required: true, message: t('please_input_firstName') },
            {
              pattern: /^(?!\s)(?!.*\s$).+$/,
              message: t('no_leading_or_trailing_spaces'),
            }
          ]}>
          <Input prefix={<UserOutlined />} placeholder={t('firstName')} style={{ borderRadius: 0 }} />
        </Form.Item>
        <Form.Item
          name='lastName'
          label={t('lastName')}
          rules={[
            { required: true, message: t('please_input_lastName') },
            {
              pattern: /^(?!\s)(?!.*\s$).+$/,
              message: t('no_leading_or_trailing_spaces'),
            }
          ]}>
          <Input prefix={<UserOutlined />} placeholder={t('lastName')} style={{ borderRadius: 0 }} />
        </Form.Item>
        <Form.Item
          name='role'
          label={t('role')}
          rules={[{ required: true, message: t('please_select_role') }]}>
          <Select showSearch placeholder={t('role')}>
            {userInfo?.role === UserRole.AltisAdmin && (
              <Select.Option value={4}>{t('AltisAdmin')}</Select.Option>
            )}
            <Select.Option value={0}>{t('Sayman')}</Select.Option>
            <Select.Option value={1}>{t('Personel')}</Select.Option>
            <Select.Option value={2}>{t('Operator')}</Select.Option>
            <Select.Option value={3}>{t('Admin')}</Select.Option>
            {/* <Select.Option value={5}>{t('AssetManager')}</Select.Option> */}
          </Select>
        </Form.Item>
        <Form.Item
          name='personelStatus'
          label={t('Personel Status')}
          rules={[{ required: true, message: t('please_select_personel_status') }]}>
          <Select showSearch placeholder={t('Personel Status')}>

            <Select.Option value={0}>{t('Active_Personnel')}</Select.Option>
            <Select.Option value={1}>{t('Allowed_Personnel')}</Select.Option>
            <Select.Option value={2}>{t('Resigner_Personnel')}</Select.Option>
            <Select.Option value={3}>{t('Dismissed_Personnel')}</Select.Option>
            <Select.Option value={4}>{t('Retired_Personnel')}</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item>
    <Row gutter={[4, 4]} justify="end" align="middle">
        <Col xs={12} sm={4}>
            <Button
                block
                onClick={handleCancel}
                type="primary"
                style={{
                    height: 64,
                    backgroundColor: 'transparent',
                    color: '#1890ff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 10px',
                    border: 'none',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                    e.currentTarget.style.color = '#40a9ff';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.color = '#1890ff';
                }}
            >
                <CloseOutlined style={{ fontSize: 20 }} />
                <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                    {t('cancel')}
                </span>
            </Button>
        </Col>
        <Col xs={12} sm={4}>
            <Button
                block
                htmlType="submit"
                type="primary"
                style={{
                    height: 64,
                    backgroundColor: 'transparent',
                    color: '#1890ff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 10px',
                    border: 'none',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                    e.currentTarget.style.color = '#40a9ff';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.color = '#1890ff';
                }}
            >
                <SaveOutlined style={{ fontSize: 20 }} />
                <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                    {t('save')}
                </span>
            </Button>
        </Col>
    </Row>
</Form.Item>
        <Modal
            title={t('password_remember')}
            visible={showPopup}  
            onCancel={() => setShowPopup(false)} 
            footer={null}  
          >
            <p>{popupMessage}</p> 
            <Button type="primary" onClick={handleRedirect}>{t('ok')}</Button>
            <Button onClick={handleCopy} style={{ marginLeft: '10px' }}>{t('copy')}</Button>
        </Modal>
      </Form>
    </Card>
  );
};

export default AddUser;
